// Eine eigenständige Section (kein Modal). Keine Props. Alle Defaults sind in der Komponente.
// Styling nutzt eure bestehenden Klassen (btn, task-form-input, task-form-label, etc.) plus wenige Inline-Styles.
import React, { useState, useMemo, useEffect } from 'react';

import {
  CrossSellCategory,
  CrossSellRelevance,
  CrossSellPayload,
} from './CrossSellingContent';

import {
  StickyCommissionBar,
  CATEGORY_OPTIONS,
  SUBCATEGORY_MAP,
  RELEVANCE_OPTIONS,
  EXTRA_INFO_TILES,
  DEFAULTS,
  formatCurrency,
  calcCommission,
} from './CrossSellingContent';

import ConfirmationStep from './ConfirmationStep';


export default function CrossSellContent() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Client (statisch vorbelegt)
  const [clientName, setClientName] = useState(DEFAULTS.clientName);
  const [clientPhone, setClientPhone] = useState<string | null>(DEFAULTS.clientPhone);
  const [clientEmail, setClientEmail] = useState<string | null>(DEFAULTS.clientEmail);

  // Consent & Submit
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ clientName?: string; consent?: string }>({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Einheitlicher GuidedFlow State
  const [guidedFlowStep, setGuidedFlowStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [note, setNote] = useState("");

  // Profil-Daten für vorausgewählte Antworten
  const profileData = {
    pflegegrad: "vorhanden",
    vorOrtTermin: "vormittags",
    extraInfo: ["kasse_vorhanden", "pflegestufe", "kontakt_betreuer"]
  };

  // Einheitliche GuidedFlow Questions - ALLE Fragen in einer Struktur
  const guidedFlowQuestions = [
    // Schritt 1: Kategorie
    {
      id: "category",
      label: "Welche Kategorie beschreibt Ihren Zusatzbedarf am besten?",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
        </svg>
      ),
      options: CATEGORY_OPTIONS.map(c => ({
        value: c.value,
        label: c.label,
        icon: c.emoji,
        score: "5"
      }))
    },

    // Schritt 2: Subkategorie (dynamisch basierend auf Kategorie)
    {
      id: "subcategory",
      label: (answers: Record<string, any>) => {
        return `Welches Produkt trifft genau zu?`;
      },
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ),
      options: (answers: Record<string, any>) => {
        const category = answers.category;
        if (!category) return [];
        const subOptions = SUBCATEGORY_MAP[category as CrossSellCategory] || [];
        if (subOptions.length === 0) {
          return [{ value: "none", label: "Kein Detail nötig", icon: "⏭️", score: "0" }];
        }
        return subOptions.map(s => ({
          value: s.value,
          label: s.label,
          icon: s.icon,
          score: "3"
        }));
      },
      condition: (answers: Record<string, any>) => {
        const category = answers.category;
        return category && SUBCATEGORY_MAP[category as CrossSellCategory]?.length > 0;
      }
    },

    {
      id: "jetzigeBadausstattung",
      label: "Jetzige Badausstattung",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 14l6-6" />
          <path d="M21 12c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1z" />
          <path d="M3 12c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1z" />
        </svg>
      ),
      options: [
        { value: "nur_dusche", label: "Nur Dusche", score: "6", icon: "🚿" },
        { value: "nur_badewanne", label: "Nur Badewanne", score: "7", icon: "🛁" },
        { value: "dusche_und_badewanne", label: "Dusche & Badewanne", score: "6", icon: "🚿🛁" }
      ],
      condition: (answers: Record<string, any>) => answers.category === "badumbau"
    },

    {
      id: "wohnsituation",
      label: "Wohnsituation",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      ),
      options: [
        { value: "eigentuemer", label: "Eigentümer", score: "7", icon: "🏠" },
        { value: "mieter", label: "Mieter", score: "3", icon: "🏢" }
      ],
      condition: (answers: Record<string, any>) => answers.category === "badumbau"
    },

    {
      id: "budgetrahmen",
      label: "Budgetrahmen",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
        </svg>
      ),
      options: [
        { value: "bis_5000", label: "bis 5.000 €", score: "5", icon: "€" },
        { value: "bis_10000", label: "bis 10.000 €", score: "7", icon: "€€" },
        { value: "ueber_10000", label: "über 10.000 €", score: "9", icon: "€€€" }
      ],
      condition: (answers: Record<string, any>) => answers.category === "badumbau"
    },
    {
      id: "restschwelle",
      label: "Restschwelle",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 3v18h18" />
          <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3" />
        </svg>
      ),
      options: [
        { value: "akzeptiert", label: "Restschwelle akzeptiert", score: "7", icon: "✓" },
        { value: "ebenerdig", label: "Ebenerdig (Fliesenarbeiten)", score: "4", icon: "🔧" }
      ],
      condition: (answers: Record<string, any>) => answers.category === "badumbau"
    },
    {
      id: "vorOrtTermin",
      label: "Vor-Ort-Termin",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      ),
      options: [
        { value: "vormittags", label: "Vormittags", score: "7", icon: "🌅" },
        { value: "nachmittags", label: "Nachmittags", score: "7", icon: "🌇" },
        { value: "nein", label: "Nein", score: "1", icon: "✗" }
      ],
      condition: (answers: Record<string, any>) => answers.category === "badumbau"
    },

    // Schritt 11: Zeitpunkt (für alle Kategorien)
    {
      id: "relevance",
      label: "Wann wird der Bedarf voraussichtlich benötigt?",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" />
        </svg>
      ),
      options: RELEVANCE_OPTIONS.map(r => ({
        value: r.value,
        label: r.label,
        icon: r.badge,
        score: "5"
      }))
    },

    // Schritt 3-10: Badumbau Details (nur bei Badumbau-Kategorie)
    {
      id: "pflegegrad",
      label: "Pflegegrad/-stufe",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" />
        </svg>
      ),
      options: [
        { value: "vorhanden", label: "Vorhanden", score: "7", icon: "✓" },
        { value: "beantragt", label: "Beantragt", score: "4", icon: "⏳" },
        { value: "nicht_vorhanden", label: "Nicht vorhanden", score: "4", icon: "✗" }
      ],
      condition: (answers: Record<string, any>) => answers.category === "badumbau"
    },
    // Schritt 12: Zusatzinfos
    {
      id: "extraInfo",
      label: "Welche Zusatzinfos treffen zu?",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
      ),
      options: EXTRA_INFO_TILES.map(t => ({
        value: t.value,
        label: t.label,
        icon: t.icon,
        score: "3"
      })),
      multiSelect: true
    }

  ];

  // Icon Rendering Helper: Vereinheitlicht Strings (Emojis) & React SVG Elemente
  function renderOptionIcon(icon: any) {
    if (typeof icon === 'string') {
      return <span style={{ fontSize: 32, lineHeight: 1 }}>{icon}</span>;
    }
    if (React.isValidElement(icon)) {
      const el = icon as React.ReactElement<any>;
      const existingStyle = (el.props && el.props.style) || {};
      return React.cloneElement(el, {
        width: (el.props && el.props.width) || 48,
        height: (el.props && el.props.height) || 48,
        style: {
          display: 'block',
          ...existingStyle,
          color: "#4D8BA5",
          fontWeight: "bold",
        }
      });
    }
    return null;
  }

  // Berechne die sichtbaren Fragen basierend auf Bedingungen
  const visibleQuestions = useMemo(() => {
    return guidedFlowQuestions.filter(question => {
      if (!question.condition) return true;
      return question.condition(answers);
    });
  }, [answers]);

  // Berechne Provision
  const commission = useMemo(
    () => calcCommission(
      answers.category ?? "sonstiges",
      answers.subcategory,
      answers.relevance
    ),
    [answers]
  );

  // Profil-Daten automatisch übernehmen, wenn das Modal geöffnet wird
  useEffect(() => {
    if (isModalOpen) {
      const preselectedAnswers: Record<string, any> = {};

      // Pflegegrad übernehmen
      preselectedAnswers.pflegegrad = profileData.pflegegrad;

      // Vor-Ort-Termin übernehmen
      preselectedAnswers.vorOrtTermin = profileData.vorOrtTermin;

      // Zusatzinfos übernehmen
      preselectedAnswers.extraInfo = [...profileData.extraInfo];

      // Kategorie auf Badumbau setzen, da Pflegegrad und Vor-Ort-Termin nur dort angezeigt werden
      preselectedAnswers.category = "badumbau";

      setAnswers(preselectedAnswers);
    }
  }, [isModalOpen]);

  // Navigation Functions
  function handleNext() {
    if (guidedFlowStep < visibleQuestions.length - 1) {
      setGuidedFlowStep(guidedFlowStep + 1);
    } else {
      // Guided Flow abgeschlossen, zur Bestätigung
      setGuidedFlowStep(visibleQuestions.length); // Completion step
    }
  }

  function handleBack() {
    if (guidedFlowStep > 0) {
      setGuidedFlowStep(guidedFlowStep - 1);
    }
  }

  function handleSelect(value: string) {
    const currentQuestion = visibleQuestions[guidedFlowStep];

    if (currentQuestion.id === "extraInfo") {
      // Multi-Select für Zusatzinfos
      setAnswers(prev => {
        const currentValues = prev.extraInfo || [];
        const newValues = currentValues.includes(value)
          ? currentValues.filter((v: string) => v !== value)
          : [...currentValues, value];
        return { ...prev, extraInfo: newValues };
      });
    } else {
      // Single-Select für alle anderen Fragen
      setAnswers(prev => ({ ...prev, [currentQuestion.id]: value }));
    }

    // Auto-advance nach Auswahl (außer bei Multi-Select)
    if (currentQuestion.id !== "extraInfo") {
      setTimeout(() => {
        handleNext();
      }, 300);
    }
  }

  function handleReset() {
    setIsSubmitted(false);
    setGuidedFlowStep(0);
    setAnswers({});
    setNote("");
    setConsent(false);
    setClientName(DEFAULTS.clientName);
    setClientPhone(DEFAULTS.clientPhone);
    setClientEmail(DEFAULTS.clientEmail);
    setErrors({});
  }

  function handleModalClose() {
    setIsModalOpen(false);
    handleReset();
  }

  async function handleSubmit() {
    const e: typeof errors = {};
    if (!clientName?.trim()) e.clientName = "Bitte Klientenname angeben.";
    if (!consent) e.consent = "Ohne Einwilligung des Klienten kann der Bedarf nicht übermittelt werden.";
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    const payload: CrossSellPayload = {
      leadId: DEFAULTS.leadId,
      clientName: clientName.trim(),
      clientPhone: clientPhone ?? undefined,
      clientEmail: clientEmail ?? undefined,
      category: (answers.category ?? "sonstiges") as CrossSellCategory,
      subCategory: answers.subcategory ?? null,
      note: note?.trim() || undefined,
      relevance: (answers.relevance ?? "spaeter") as CrossSellRelevance,
      consentGranted: consent,
      // Badumbau Details hinzufügen, wenn vorhanden
      ...(answers.category === "badumbau" && {
        badumbauDetails: {
          pflegegrad: answers.pflegegrad,
          wohnsituation: answers.wohnsituation,
          einverstaendnisVermieter: answers.einverstaendnisVermieter,
          jetzigeBadausstattung: answers.jetzigeBadausstattung,
          budgetrahmen: answers.budgetrahmen,
          restschwelle: answers.restschwelle,
          vorOrtTermin: answers.vorOrtTermin,
          bedarf: answers.bedarf,
        }
      })
    };

    try {
      setSubmitting(true);
      // Demo: ersetze dies durch echten API-Call
      await new Promise((r) => setTimeout(r, 600));
      console.log("CrossSell submitted:", payload);
      setIsSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  }

  // Hilfsfunktion für dynamische Labels
  function getQuestionLabel(question: any) {
    if (typeof question.label === 'function') {
      return question.label(answers);
    }
    return question.label;
  }

  // Hilfsfunktion für dynamische Options
  function getQuestionOptions(question: any) {
    if (typeof question.options === 'function') {
      return question.options(answers);
    }
    return question.options;
  }


  function OptionGrid({ options, render }: { options: any[]; render: (o: any) => React.ReactNode }) {
    const gridTemplateColumns = useMemo(() => {
      if (options.length === 2 || options.length === 4) {
        return "repeat(2, 1fr)";
      }
      return "repeat(3, 1fr)";
    }, [options.length]);

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns,
          gap: 12,
          width: "100%",
        }}
      >
        {options.map(render)}
      </div>
    );
  }


  // ---- Render: Minimal Section with Modal ----
  return (
    <section
      aria-labelledby="crossSellTitle"
      style={{ margin: "0 auto" }}
    >
      {/* Minimal initial view */}
      {!isModalOpen && (
        <div style={{ textAlign: "left" }}>
          <p style={{ marginBottom: "12px", fontSize: "16px", lineHeight: "1.5" }}>
            Ihr Klient könnte einen <strong>zusätzlichen Bedarf</strong> haben –
            erfassen Sie ihn jetzt direkt in unserem System.
            <br />
            <strong>Verdienen Sie sich eine Provision</strong> für jede erfolgreiche Vermittlung
            und unterstützen Sie Ihren Klienten optimal mit passenden Lösungen.
          </p>

          <div
            style={{
              marginBottom: "16px",
              padding: "12px 16px",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              backgroundColor: "#f9fafb",
              fontSize: "14px",
              color: "#374151",
            }}
          >
            🔍 Wahrscheinlichkeits-Matching:
            <strong> 82% Trefferchance</strong> auf passende Zusatzangebote für Ihren Klienten.
          </div>

          <button
            type="button"
            className="btn btn-primary btn-medium-small"
            onClick={() => setIsModalOpen(true)}
            style={{ padding: "12px 24px", fontSize: "16px" }}
          >
            Zusatzbedarf jetzt erfassen
          </button>
        </div>
      )}

      {/* Modal Overlay */}
      {isModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.76)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 4000,
            padding: "20px"
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleModalClose();
            }
          }}
        >
          {/* Modal Content */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              maxWidth: "1200px",
              width: "100%",
              maxHeight: "100vh",
              overflowY: "auto",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.17)"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}
            >
              <div >  </div>
              <h2 className="task-modal-title text-black" style={{ margin: 0, fontSize: "24px" }}>
                Zusatzbedarf erfassen
              </h2>
              <button
                type="button"
                onClick={handleModalClose}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#6b7280",
                  padding: "4px"
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Body - Guided Flow */}
            <div style={{ padding: "24px" }}>



              {/* Guided Flow Questions */}
              {!isSubmitted && guidedFlowStep < visibleQuestions.length && (
                <>

                  <div style={{ marginBottom: 32 }}>
                    <div style={{
                      height: "8px",
                      backgroundColor: "#f3f4f6",
                      borderRadius: "4px",
                      overflow: "hidden"
                    }}>
                      <div
                        style={{
                          height: "100%",
                          width: `${(guidedFlowStep) / visibleQuestions.length * 100}%`,
                          backgroundColor: "#3b82f6",
                          borderRadius: "4px",
                          transition: "width 0.3s ease"
                        }}
                      />
                    </div>
                  </div>

                  {/* Guided Flow Progress */}

                  {/* Current Question */}
                  <div style={{
                    backgroundColor: "#f8fafc",
                    borderRadius: "16px",
                    padding: "32px",
                    border: "1px solid #e2e8f0",
                    minHeight: "400px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center"
                  }}>
                    <div style={{
                      textAlign: "center",
                      marginBottom: 32,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 12
                    }}>
                      <div style={{
                        width: "64px",
                        height: "64px",
                        backgroundColor: "#dbeafe",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#1d4ed8"
                      }}>
                        {visibleQuestions[guidedFlowStep].icon}
                      </div>
                      <h3 style={{
                        fontSize: 24,
                        fontWeight: 700,
                        color: "#1f2937",
                        margin: 0
                      }}>
                        {getQuestionLabel(visibleQuestions[guidedFlowStep])}
                      </h3>
                    </div>

                    {/* Options */}
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: (() => {
                        const currentOptions = getQuestionOptions(visibleQuestions[guidedFlowStep]);
                        const optionCount = currentOptions.length;

                        if (optionCount === 2 || optionCount === 4) {
                          return "repeat(2, 1fr)";
                        } else {
                          return "repeat(3, 1fr)";
                        }
                      })(),
                      gap: "16px",
                      maxWidth: "800px",
                      margin: "0 auto",
                      width: "100%"
                    }}>
                      {getQuestionOptions(visibleQuestions[guidedFlowStep]).map((option: any) => {
                        const currentQuestion = visibleQuestions[guidedFlowStep];
                        const isSelected = currentQuestion.id === "extraInfo"
                          ? answers.extraInfo?.includes(option.value)
                          : answers[currentQuestion.id] === option.value;

                        // Prüfen, ob diese Option aus dem Profil übernommen wurde
                        const isFromProfile =
                          (currentQuestion.id === "pflegegrad" && profileData.pflegegrad === option.value) ||
                          (currentQuestion.id === "vorOrtTermin" && profileData.vorOrtTermin === option.value) ||
                          (currentQuestion.id === "extraInfo" && profileData.extraInfo?.includes(option.value));

                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => handleSelect(option.value)}
                            style={{
                              padding: "24px 16px",
                              borderRadius: "16px",
                              border: isSelected ? "2px solid #3b82f6" : "2px solid #e5e7eb",
                              background: isSelected ? "#eff6ff" : "#ffffff",
                              boxShadow: isSelected ? "0 4px 14px rgba(59, 130, 246, 0.18)" : "0 2px 8px rgba(0, 0, 0, 0.06)",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              textAlign: "center",
                              minHeight: currentQuestion.id === "extraInfo" ? "100px" : "120px",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "12px",
                              position: "relative",
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.transform = "translateY(-2px)";
                                e.currentTarget.style.boxShadow = "0 8px 25px rgba(0, 0, 0, 0.1)";
                                e.currentTarget.style.borderColor = "#3b82f6";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.05)";
                                e.currentTarget.style.borderColor = "#e5e7eb";
                              }
                            }}
                          >
                            <div style={{ fontSize: 0 }}>
                              {renderOptionIcon(option.icon)}
                            </div>
                            <div style={{ fontSize: "16px", fontWeight: 600, color: "#374151" }}>
                              {option.label}
                            </div>

                            {/* Badge für Profil-übernommene Antworten */}
                            {isFromProfile && (
                              <div style={{
                                position: "absolute",
                                top: "8px",
                                right: "8px",
                                backgroundColor: "#10b981",
                                color: "#ffffff",
                                fontSize: "10px",
                                fontWeight: 600,
                                padding: "2px 6px",
                                borderRadius: "12px",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}>
                                Aus Profil
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {/* Navigation */}
                    <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: "32px",
                      paddingTop: "24px",
                      borderTop: "1px solid #e2e8f0"
                    }}>
                      <button
                        type="button"
                        onClick={handleBack}
                        disabled={guidedFlowStep === 0}
                        style={{
                          padding: "12px 24px",
                          borderRadius: "8px",
                          border: "1px solid #d1d5db",
                          background: guidedFlowStep === 0 ? "#f9fafb" : "#ffffff",
                          color: guidedFlowStep === 0 ? "#9ca3af" : "#374151",
                          fontSize: "14px",
                          fontWeight: 500,
                          cursor: guidedFlowStep === 0 ? "not-allowed" : "pointer",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          if (guidedFlowStep > 0) {
                            e.currentTarget.style.background = "#f3f4f6";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = guidedFlowStep === 0 ? "#f9fafb" : "#ffffff";
                        }}
                      >
                        ← Zurück
                      </button>

                      <div style={{ fontSize: "12", color: "#6b7280", textAlign: "center" }}>
                        {visibleQuestions[guidedFlowStep].id === "extraInfo"
                          ? "Mehrfachauswahl möglich - Klicken Sie auf Weiter wenn fertig"
                          : "Klicken Sie auf eine Option um fortzufahren"
                        }
                      </div>

                      {visibleQuestions[guidedFlowStep].id === "extraInfo" ? (
                        <button
                          type="button"
                          onClick={handleNext}
                          style={{
                            padding: "12px 24px",
                            borderRadius: "8px",
                            border: "none",
                            background: "#10b981",
                            color: "#ffffff",
                            fontSize: "14px",
                            fontWeight: 500,
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#059669";
                            e.currentTarget.style.transform = "translateY(-1px)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#10b981";
                            e.currentTarget.style.transform = "translateY(0)";
                          }}
                        >
                          Weiter →
                        </button>
                      ) : (
                        <div style={{ width: "100px" }}></div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Note Input Screen */}
              {!isSubmitted && guidedFlowStep >= visibleQuestions.length && answers.note === "ja" && (
                <div style={{
                  backgroundColor: "#f8fafc",
                  borderRadius: "16px",
                  padding: "32px",
                  border: "1px solid #e2e8f0",
                  minHeight: "400px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center"
                }}>
                  <div style={{
                    textAlign: "center",
                    marginBottom: 32,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 12
                  }}>
                    <div style={{
                      width: "64px",
                      height: "64px",
                      backgroundColor: "#dbeafe",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#1d4ed8"
                    }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <polyline points="10 9 9 9 8 9" />
                      </svg>
                    </div>
                    <h3 style={{
                      fontSize: 24,
                      fontWeight: 700,
                      color: "#1f2937",
                      margin: 0
                    }}>
                      Kurze Beschreibung hinzufügen
                    </h3>
                    <p style={{ fontSize: 16, color: "#6b7280", margin: 0 }}>
                      Beschreiben Sie den Bedarf kurz in Ihren eigenen Worten
                    </p>
                  </div>

                  <div style={{ maxWidth: "600px", margin: "0 auto", width: "100%" }}>
                    <textarea
                      id="csNote"
                      className="task-form-textarea"
                      placeholder='z. B. "kommt die Treppe nicht mehr hoch", "benötigt Hilfe beim Duschen" etc.'
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={4}
                      style={{
                        width: "100%",
                        padding: "16px",
                        borderRadius: "8px",
                        border: "1px solid #d1d5db",
                        fontSize: "14px",
                        resize: "vertical",
                        minHeight: "120px"
                      }}
                    />
                  </div>

                  <div style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginTop: "32px",
                    paddingTop: "24px",
                    borderTop: "1px solid #e2e8f0"
                  }}>
                    <button
                      type="button"
                      onClick={() => setGuidedFlowStep(visibleQuestions.length + 1)}
                      style={{
                        padding: "12px 32px",
                        borderRadius: "8px",
                        border: "none",
                        background: "#10b981",
                        color: "#ffffff",
                        fontSize: "16px",
                        fontWeight: 600,
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                    >
                      Weiter zum nächsten Schritt
                    </button>
                  </div>
                </div>
              )}


              {/* Bestätigungs-Step - Modern Design */}
              {!isSubmitted && guidedFlowStep >= visibleQuestions.length && (answers.note !== "ja" || note) && guidedFlowStep < visibleQuestions.length + 1 && (
                <ConfirmationStep
                  clientName={clientName}
                  setClientName={setClientName}
                  clientPhone={clientPhone}
                  setClientPhone={setClientPhone}
                  clientEmail={clientEmail}
                  setClientEmail={setClientEmail}
                  consent={consent}
                  setConsent={setConsent}
                  errors={errors}

                />

              )}

              {/* Success Screen */}
              {isSubmitted && (
                <div style={{ border: "1px solid #d1fae5", background: "#ecfdf5", borderRadius: 14, padding: "20px", marginTop: 16 }}>
                  <div style={{ fontSize: "18px", fontWeight: "bold", color: "#065f46", marginBottom: "16px" }}>
                    Vielen Dank! Der Bedarf wurde erfolgreich erfasst.
                  </div>
                  <div style={{ display: "grid", gap: "12px" }}>
                    <div><strong>Klient:</strong> {clientName}</div>
                    <div>
                      <strong>Kategorie:</strong> {CATEGORY_OPTIONS.find(c => c.value === answers.category)?.label || answers.category}
                      {answers.subcategory && answers.subcategory !== "none" && (
                        <span> - {SUBCATEGORY_MAP[answers.category as CrossSellCategory]?.find(s => s.value === answers.subcategory)?.label || answers.subcategory}</span>
                      )}
                    </div>
                    <div><strong>Zeitpunkt:</strong> {RELEVANCE_OPTIONS.find(r => r.value === answers.relevance)?.label || answers.relevance}</div>
                    {note && <div><strong>Notiz:</strong> {note}</div>}
                    <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #a7f3d0" }}>
                      <div style={{ fontSize: "16px", fontWeight: "bold", color: "#065f46" }}>
                        Ihre potenzielle Provision: {formatCurrency(commission)}
                      </div>
                      <div style={{ fontSize: "12px", color: "#047857" }}>
                        Hinweis: Abhängig von Abschluss & Produktvariante.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: "16px 24px",
                borderTop: "1px solid #e5e7eb",
                backgroundColor: "#f9fafb"
              }}
            >
              {!isSubmitted ? (
                <StickyCommissionBar
                  amount={commission}
                  onBack={guidedFlowStep > 0 ? handleBack : undefined}
                  onNext={
                    guidedFlowStep < visibleQuestions.length && visibleQuestions[guidedFlowStep].id === "extraInfo"
                      ? handleNext
                      : undefined
                  }
                  onSubmit={guidedFlowStep >= visibleQuestions.length ? handleSubmit : undefined}
                  canNext={
                    guidedFlowStep < visibleQuestions.length && visibleQuestions[guidedFlowStep].id === "extraInfo"
                      ? true // ExtraInfo kann immer weiter
                      : undefined
                  }
                  canSubmit={guidedFlowStep >= visibleQuestions.length ? !!clientName?.trim() && consent : undefined}
                  submitting={submitting}
                />
              ) : (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-primary btn-medium-small"
                    onClick={handleModalClose}
                  >
                    Schließen
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
